import { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import qs from 'qs';
import {
  Discovery,
  SiteDiscoveryValues,
  SiteFacetsConfigData,
} from '../../../store/features/discovery';
import { useForm } from 'react-hook-form';
import { DiscoveryContentTypes } from '../../../constants';
import Drawer from '@mui/material/Drawer';
import {
  SiteSearchProvider,
  SearchContentPreviewGrid,
  ResetFilterButton,
  KeywordSearch,
  MobileFilterButton,
  NoSiteSearchResults,
  ContentTypeBuilder,
  FilterFacets,
  LoaderWrapper,
} from './components';
import { CONTENT_TYPES_DATA_MAP } from './constants';
import { useSiteSearchFilter } from './hooks/useSiteSearchFilter';
import { useTranslation } from 'react-i18next';
import { useRootSelector } from '../../../store/hooks';
import { selectPageConfig } from '../../../store/features/pageConfig';
import styles from './SiteSearchPageLayout.module.scss';

export const onChangeFilter = (
  data: SiteDiscoveryValues,
  containerRef: React.RefObject<HTMLDivElement>,
  mainMenuHeight: number,
  changeFilterHandler: (data: SiteDiscoveryValues) => void,
) => {
  const timeout = window.scrollY > window.innerHeight ? 100 : 0;

  if (
    containerRef.current?.offsetTop &&
    window.scrollY > containerRef.current?.offsetTop
  ) {
    document.documentElement.scrollTop =
      containerRef.current.offsetTop - mainMenuHeight;
  }

  setTimeout(() => {
    changeFilterHandler(data);
  }, timeout);
};

export interface SiteSearchPageLayoutProps {
  facets: Discovery['facets'];
  records: Discovery['records'];
  defaultValues: SiteDiscoveryValues;
  searchQueryData?: SiteFacetsConfigData;
}

const MAIN_MENU_HEIGHT = 55;

export default function SiteSearchPageLayout(props: SiteSearchPageLayoutProps) {
  const { facets, records, defaultValues, searchQueryData } = props;
  const { t } = useTranslation();
  const [openFilterSidebar, setOpenFiltersSidebar] = useState(false);
  const { search } = useLocation();
  const { elementsProperties } = useRootSelector(selectPageConfig);
  const mainMenuHeight =
    elementsProperties?.multiLevelNavMenu?.height || MAIN_MENU_HEIGHT;
  const containerRef = useRef<HTMLDivElement>(null);

  const initialFilterValues = qs.parse(search, {
    ignoreQueryPrefix: true,
  });

  const form = useForm<SiteDiscoveryValues>({
    defaultValues: {
      ...defaultValues,
      ...initialFilterValues,
    },
  });

  const handleOpenFilterSidebar = (open: boolean) => () => {
    setOpenFiltersSidebar(open);
  };

  const types = form.watch('type') || [];
  const contentType = types[0] || DiscoveryContentTypes.ALL;
  const initialValue = initialFilterValues.searchInput?.toString();

  const {
    loading,
    changeFilterHandler,
    resetFilterHandler,
    resetTextFilterHandler,
    loadMoreFilterResultsHandler,
    keywordSearchProps,
  } = useSiteSearchFilter({
    form,
    contentType,
    defaultValues,
    initialValue,
    searchQueryData,
  });

  const allSearchResults = records.filter(
    (record) => record.results.length > 0,
  );

  const getPreviewSearchContent = () => {
    return allSearchResults.map((searchGroup) => {
      const { type } = searchGroup;
      return (
        <SearchContentPreviewGrid
          key={type}
          heading={CONTENT_TYPES_DATA_MAP[type]?.heading}
        >
          <ContentTypeBuilder
            contentType={type}
            loadMoreFilterResults={loadMoreFilterResultsHandler}
            enableLoadingByScroll={contentType !== DiscoveryContentTypes.ALL}
          />
        </SearchContentPreviewGrid>
      );
    });
  };

  const renderFiltersSidebar = (
    <>
      <div className={cn('hidden-xs hidden-sm', styles.searchBar)}>
        <KeywordSearch
          {...keywordSearchProps}
          onResetValue={resetTextFilterHandler}
          form={form}
        />
      </div>

      <div className={styles.filtersHeading}>
        <div className={styles.filters}>{t('site.search.filters')}</div>
        {resetFilterHandler && (
          <div className={styles.resetFilter}>
            <ResetFilterButton onClick={resetFilterHandler} disabled={false}>
              {t('site.search.reset-filters')}
            </ResetFilterButton>
          </div>
        )}
      </div>
      <FilterFacets facets={facets} />
    </>
  );

  return (
    <SiteSearchProvider
      form={form}
      onChangeFilter={(data) =>
        onChangeFilter(data, containerRef, mainMenuHeight, changeFilterHandler)
      }
    >
      <div
        className={cn('container-fluid', styles.mainSection)}
        ref={containerRef}
      >
        <div className="row">
          <div
            data-testid="sidebar-left-section"
            style={{
              top: mainMenuHeight,
              height: `calc(100vh - ${mainMenuHeight}px)`,
            }}
            className={cn(
              'col-lg-offset-1 col-md-4 col-lg-3 hidden-xs hidden-sm',
              styles.leftSection,
            )}
          >
            {renderFiltersSidebar}
          </div>
          <div
            className={cn('col-xs-12 col-md-8 col-lg-7', styles.rightSection)}
          >
            <div
              className={cn('hidden-md hidden-lg', styles.searchBarSticky)}
              style={{
                top: mainMenuHeight,
              }}
            >
              <MobileFilterButton
                iconName="tune"
                onClick={handleOpenFilterSidebar(true)}
              >
                {t('site.search.filters')}
              </MobileFilterButton>
              <KeywordSearch
                {...keywordSearchProps}
                onResetValue={resetTextFilterHandler}
                form={form}
              />
            </div>
            <LoaderWrapper loading={loading}>
              {allSearchResults.length === 0 ? (
                <NoSiteSearchResults />
              ) : (
                getPreviewSearchContent()
              )}
            </LoaderWrapper>
          </div>
          <div
            className={`col-lg-1 hidden-xs hidden-sm hidden-md ${styles.offset}`}
          />
        </div>
      </div>
      <Drawer
        anchor="left"
        open={openFilterSidebar}
        onClose={handleOpenFilterSidebar(false)}
        data-testid="filter-sidebar"
      >
        <div className={styles.mobileWrapper}>{renderFiltersSidebar}</div>
      </Drawer>
    </SiteSearchProvider>
  );
}
